import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  Checkbox,
  InputLabel,
  Grid,
  TableRow,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  Paper,
  TableBody,
  IconButton,
  Menu,
  Backdrop,
  CircularProgress,
  FormControl,
  Chip,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  deleteSuperBillByBiller,
  getBillingReportByBiller,
  downloadBillingReportByBiller,
  getFacilityByProviderIds,
  getProviderListByFacility,
  getProvidersByBillerId,
  updateMultipleReportByBillerId,
  updateReportByBillerId,
} from "../../../api/action";
import { useDispatch, useSelector } from "react-redux";
import CustomSnackbar from "../../../Utils/CustomSnackbar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModal from "../../../Utils/DeleteModal";
import CustomPagination from "../../../Utils/CustomPagination";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // don't forget styles

const headers = [
  "Provider",
  "Facility",
  "Facility Type",
  "First Name",
  "Last Name",
  "Date of Birth",
  "Date of Service",
  "Date of Submission",
  "CPT Code",
  "DX Code",
  "Admission",
  "Reason for Visit",
  "Comment",
  "Modifier",
  "Status",
  "Biller",
  "Action",
];

const BillerReportsBody = () => {
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [providerList, setProviderList] = useState([]);
  const [facilityList, setFacilityList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [results, setResults] = useState([]);
  const dispatch = useDispatch();
  const billerId = useSelector((state) => state?.user?.userId);
  const isDeletedAccess = useSelector((state) => state?.user?.isDeletedAccess);
  const [anchorEl, setAnchorEl] = useState(null);
  const [rowId, setRowId] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteBillId, setDeleteBillId] = useState(null);
  const [updated, setUpdated] = useState(false);
  const [counts, setCounts] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [editMultipleStatus, setEditMultipleStatus] = useState("");
  const [anchorEl1, setAnchorEl1] = useState(null);

  const handleClick = (event) => {
    setAnchorEl1(event.currentTarget);
  };
  const ROWS_PER_PAGE = 50;

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows(results.map((row) => row.id));
      setSelectAll(true);
    } else {
      setSelectedRows([]);
      setSelectAll(false);
    }
  };

  const saveBlobFile = (blob, filename = "BillerReports.xlsx") => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const showDownloadError = (error) => {
    const fallback = "Download failed. Please try again.";
    const blob = error?.response?.data;
    const applyMessage = (message) => {
      setSnackbarMessage(message || fallback);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    };

    if (blob instanceof Blob) {
      blob
        .text()
        .then((text) => {
          try {
            const json = JSON.parse(text);
            applyMessage(json.error || json.message || fallback);
          } catch {
            applyMessage(fallback);
          }
        })
        .catch(() => applyMessage(fallback));
      return;
    }

    if (
      error?.code === "ERR_NETWORK" ||
      error?.code === "ECONNABORTED" ||
      String(error?.message || "").includes("HTTP2")
    ) {
      applyMessage("Download timed out or failed. Please try again.");
      return;
    }

    applyMessage(error?.response?.data?.error || fallback);
  };

  const handleDownloadExcel = () => {
    setLoading(true);
    const payload = {
      providers: selectedProviders.map((p) => p.id),
      facilities: selectedFacilities,
      patient_first_name: firstName || "",
      patient_last_name: lastName || "",
      status: status || "",
      date_from: dateFrom || "",
      date_to: dateTo || "",
      billerId: billerId,
      download_excel: true,
    };

    dispatch(
      downloadBillingReportByBiller(
        payload,
        (response) => {
          setLoading(false);
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          saveBlobFile(blob, "BillerReports.xlsx");
          setSelectedRows([]);
          setSelectAll(false);
        },
        (error) => {
          console.log("Error fetching billing report:", error);
          setLoading(false);
          showDownloadError(error);
        }
      )
    );
  };

  // ✅ Handle single row select
  const handleRowSelect = (id) => {
    setSelectedRows((prev) => {
      const newSelection = prev.includes(id)
        ? prev.filter((rowId) => rowId !== id) // deselect
        : [...prev, id]; // select

      // if every row is selected → setSelectAll true
      if (newSelection.length === results.length) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }

      return newSelection;
    });
  };

  // ✅ Helper to check if a row is selected
  const isSelected = (id) => selectedRows.includes(id);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    setSelectedRows([]);
    setSelectAll(false);
  };

  useEffect(() => {
    if (currentPage) {
      handleSearch();
    }
  }, [currentPage]);

  useEffect(() => {
    dispatch(
      getProvidersByBillerId(
        null,
        billerId,
        (data) => {
          setLoading(false);
          setProviderList(data?.data?.Data || []);
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      )
    );
  }, []);

  useEffect(() => {
    const payload = {
      facility_ids: selectedFacilities,
      biller_id: billerId,
    };
    dispatch(
      getProviderListByFacility(
        payload,
        (data) => {
          setLoading(false);
          // setProviderList(data?.data?.Data || []);
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      )
    );
  }, [selectedFacilities]);

  useEffect(() => {
    if (selectedProviders.length > 0) {
      const payload = { user_id: selectedProviders.map((p) => p.provider_id) };
      dispatch(
        getFacilityByProviderIds(
          payload,
          (data) => {
            setLoading(false);
            setFacilityList(data?.data?.Data || []);
          },
          (error) => {
            console.log(error);
            setLoading(false);
          }
        )
      );
    } else {
      // ✅ Reset facilities when no provider is selected
      setFacilityList([]);
      setSelectedFacilities([]);
    }
  }, [selectedProviders]);

  const handleSearch = () => {
    setLoading(true);

    if (
      selectedProviders.length === 0 &&
      selectedFacilities.length === 0 &&
      !firstName &&
      !lastName &&
      !dateFrom &&
      !dateTo
    ) {
      setLoading(false);
      setSnackbarMessage("Search fields should not be empty");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = {
      providers: selectedProviders.map((p) => p.id),
      facilities: selectedFacilities,
      patient_first_name: firstName || "",
      patient_last_name: lastName || "",
      status: status || "",
      date_from: dateFrom || "",
      date_to: dateTo || "",
      billerId: billerId,
      download_excel: false,
    };

    dispatch(
      getBillingReportByBiller(
        payload,
        currentPage,
        (data) => {
          setLoading(false);
          setResults(data?.data?.results || []);

          setCounts(data?.data?.count);
        },
        (error) => {
          console.log("Error fetching billing report:", error);
          setLoading(false);
          setSnackbarMessage(error?.response?.data?.error);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  const handleClear = () => {
    setSelectedProviders([]);
    setSelectedFacilities([]);
    setFirstName("");
    setLastName("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
  };

  // -------- Edit Actions --------
  const handleEditClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setRowId(row.id);
  };

  const handleEditClose = () => {
    setAnchorEl(null);
  };

  const handleEditStatus = (newStatus) => {
    setLoading(true);
    const payload = {
      biller_id: billerId,
      superbiller_id: rowId,
      is_status: newStatus,
    };

    dispatch(
      updateReportByBillerId(
        payload,
        () => {
          setLoading(false);
          setSnackbarMessage("Status Updated Successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          handleEditClose();
          handleSearch(); // 🔄 refresh table with latest results
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      )
    );
  };

  // -------- Delete Action --------
  const handleDeleteConfirm = () => {
    setLoading(true);
    const payload = {
      biller_id: billerId,
      super_biller_id: deleteBillId,
    };

    dispatch(
      deleteSuperBillByBiller(
        payload,
        () => {
          setLoading(false);
          setSnackbarMessage("Superbill Deleted Successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setUpdated((prev) => !prev);
          setOpenDeleteModal(false);
          handleSearch(); // 🔄 refresh table with latest results
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      )
    );
  };

  const handleClose = () => {
    setAnchorEl1(null);
  };

  const handleMenuItemClick = (value) => {
    setEditMultipleStatus(value);
    handleMultipleEditStatus(value);
    handleClose();
  };

  const handleMultipleEditStatus = (newStatus) => {
    setLoading(true);
    const payload = {
      biller_id: billerId,
      superbiller_id: selectedRows,
      is_status: newStatus,
    };
    dispatch(
      updateMultipleReportByBillerId(
        payload,
        (data) => {
          setSelectAll(false);
          setLoading(false);
          setSnackbarMessage(data?.data?.message);
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setUpdated((prevState) => !prevState);
          setSelectedRows([]);
          setEditMultipleStatus("Edit Status");
          handleEditClose();
          handleSearch();
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      )
    );
  };

  const commonInputSx = {
    // width:"100% !important",
    // minWidth:"10rem !important",
    "& .MuiOutlinedInput-root": {
      minHeight: "34px !important",
      height: "100% !important",
      fontSize: "12px",
      padding: "4px 10px !important",
    },
    "& .MuiOutlinedInput-input": {
      padding: "0px !important",
      fontSize: "12px",
    },
    "& .MuiAutocomplete-root": {
      height: "28px",
    },
  };

  // Utility: format date in MM/DD/YYYY without timezone shift
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  return (
    <Box p={3}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, mb: 2, color: "#1d3969" }}
      >
        Billing Report
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} mb={3}>
        {/* Provider */}
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <InputLabel>Provider</InputLabel>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={[{ id: "ALL", name: "Select All" }, ...providerList]}
            getOptionLabel={(option) => option.name}
            // keep full object (id + provider_id) in state instead of just id
            value={selectedProviders}
            onChange={(e, newValue) => {
              if (newValue.some((v) => v.id === "ALL")) {
                setSelectedProviders(providerList); // ✅ keep full objects
              } else {
                setSelectedProviders(newValue); // ✅ keep full objects
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={option.name}
                  size="small"
                  sx={{ fontSize: "12px", height: 22 }}
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={
                    option.id === "ALL"
                      ? selectedProviders.length === providerList.length
                      : selected
                  }
                />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                // sx={{
                //   "& input": { height: "18px !important", fontSize: "14px" },
                // }}
                sx={commonInputSx}
              />
            )}
          />
        </Grid>

        {/* Facility */}
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <InputLabel>Facility Name</InputLabel>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={
              facilityList.length > 0
                ? [
                    {
                      id: "ALL",
                      facility_name: "Select All",
                      facility_code: "",
                    },
                    ...facilityList,
                  ]
                : [] // ✅ no options when list is empty
            }
            getOptionLabel={(option) => option.facility_name}
            value={facilityList.filter((f) =>
              selectedFacilities.includes(f.id)
            )}
            onChange={(e, newValue) => {
              if (newValue.some((v) => v.id === "ALL")) {
                // ✅ Select all facilities
                setSelectedFacilities(facilityList.map((f) => f.id));
              } else {
                setSelectedFacilities(newValue.map((f) => f.id));
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  label={`${option.facility_name} ${
                    option.facility_code ? `(${option.facility_code})` : ""
                  }`}
                  size="small"
                  sx={{ fontSize: "12px", height: 22 }}
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={
                    option.id === "ALL"
                      ? selectedFacilities.length === facilityList.length
                      : selected
                  }
                />
                {option.facility_name}{" "}
                {option.facility_code && `(${option.facility_code})`}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                // sx={{
                //   "& input": { height: "18px !important", fontSize: "14px" },
                // }}
                sx={commonInputSx}
              />
            )}
          />
        </Grid>

        {/* First Name */}
        <Grid item xs={12} sm={6} md={2.3} lg={2.3}>
          <InputLabel>Patient First Name</InputLabel>
          <TextField
            size="small"
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Grid>

        {/* Last Name */}
        <Grid item xs={12} sm={6} md={2.3} lg={2.3}>
          <InputLabel>Patient Last Name</InputLabel>
          <TextField
            size="small"
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6} md={2.3} lg={2.3}>
          <InputLabel>Status</InputLabel>
          <TextField
            select
            size="small"
            fullWidth
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">Select Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={2.5} lg={2.5}>
          <InputLabel>Date From / DoS</InputLabel>
          <DatePicker
            selected={dateFrom ? new Date(dateFrom) : null}
            onChange={(date) => setDateFrom(formatDate(date))}
            // placeholderText="Select start date"
            className="reactDateRangePicker3"
            dateFormat="MM/dd/yyyy"
            isClearable
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2.5} lg={2.5}>
          <InputLabel>Date To / DoS</InputLabel>
          <DatePicker
            selected={dateTo ? new Date(dateTo) : null}
            onChange={(date) => setDateTo(formatDate(date))}
            // placeholderText="Select end date"
            className="reactDateRangePicker3"
            dateFormat="MM/dd/yyyy"
            minDate={dateFrom ? new Date(dateFrom) : null}
            isClearable
          />
        </Grid>
      </Grid>

      {/* Date Filters */}

      {/* Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          onClick={handleClear}
          sx={{
            backgroundColor: "#1d3969",
            textTransform: "none",
            borderRadius: "6px",
            px: 3,
          }}
        >
          Clear
        </Button>
        <Button
          onClick={handleSearch}
          sx={{
            backgroundColor: "#1d3969",
            textTransform: "none",
            borderRadius: "6px",
            px: 3,
          }}
        >
          Search
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          verticalAlign: "middle",
          mt: 5,
        }}
      >
        <FormControl sx={{ mr: 2 }}>
          <Button
            sx={{ width: "100%" }}
            variant="contained"
            onClick={handleClick}
            disabled={selectedRows.length === 0}
          >
            {editMultipleStatus || "Edit Status"}
          </Button>
          <Menu
            id="edit-status-menu"
            anchorEl={anchorEl1}
            open={Boolean(anchorEl1)}
            onClose={handleClose}
          >
            {/* <MenuItem onClick={() => handleMenuItemClick('Approved')}>Approved</MenuItem> */}
            <MenuItem onClick={() => handleMenuItemClick("Pending")}>
              Pending
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick("Confirmed")}>
              Confirmed
            </MenuItem>
          </Menu>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleDownloadExcel}
          disabled={selectedRows.length === 0} // ✅ disable when nothing selected
          sx={{
            backgroundColor:
              selectedRows.length === 0 ? "#ccc" : "rgba(29, 57, 105, 1)", // grey when disabled
            textTransform: "none",
            borderRadius: "6px",
            px: 3,
            "&.Mui-disabled": {
              backgroundColor: "#ccc",
              color: "#666",
            },
          }}
        >
          Download Excel
        </Button>
      </Box>

      {/* ✅ Table for results */}
      <TableContainer component={Paper} sx={{ mt: 4, minHeight: "60vh" }}>
        <Table
          size="small"
          sx={{ "& td, & th": { py: 0.5, px: 1, fontSize: "13px" } }}
        >
          <TableHead sx={{ background: "rgba(29, 57, 105, 1)" }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  // indeterminate={
                  //   selectedRows.length > 0 &&
                  //   selectedRows.length < results.length
                  // }
                  checked={selectAll} // ✅ controlled by state
                  onChange={handleSelectAllClick}
                  sx={{
                    color: "#fff",
                    "&.Mui-checked": { color: "#fff" }, // checked
                    // "&.MuiCheckbox-indeterminate": { color: "#fff" }, // indeterminate
                    "& .MuiSvgIcon-root": { fontSize: 20 },
                  }}
                />
              </TableCell>
              {headers.map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    color: "#fff",
                    whiteSpace: "nowrap",
                    fontWeight: "600",
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              results.map((row) => (
                <TableRow key={row.id} sx={{ height: "32px" }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isSelected(row.id)}
                      onChange={() => handleRowSelect(row.id)}
                    />
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.provider_profile}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.facility_master}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.facility_type}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.patient_first_name}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.patient_last_name}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.dob}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.date_of_service}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.submitted_datetime}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.cpt_code}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.dx_code}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.admission}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.reason_for_visit}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.comment}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.modifier}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.is_status}
                  </TableCell>
                  <TableCell className="fs12" sx={{ whiteSpace: "nowrap" }}>
                    {row.biller_profile}
                  </TableCell>
                  <TableCell sx={{ display: "flex", whiteSpace: "nowrap" }}>
                    {/* Edit */}
                    <IconButton
                      color="primary"
                      onClick={(e) => handleEditClick(e, row)}
                    >
                      <EditIcon
                        sx={{
                          color: "rgba(29, 57, 105, 1)",
                          fontSize: "16px",
                          my: 1,
                        }}
                      />
                    </IconButton>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleEditClose}
                      PaperProps={{
                        elevation: 0, // removes shadow
                        sx: {
                          border: "1px solid #ddd",
                        },
                      }}
                    >
                      <MenuItem onClick={() => handleEditStatus("Confirmed")}>
                        Confirmed
                      </MenuItem>
                      <MenuItem onClick={() => handleEditStatus("Pending")}>
                        Pending
                      </MenuItem>
                    </Menu>

                    {/* Delete */}
                    {isDeletedAccess === "true" && (
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setDeleteBillId(row.id);
                          setOpenDeleteModal(true);
                        }}
                      >
                        <DeleteIcon
                          sx={{
                            color: "rgba(29, 57, 105, 1)",
                            fontSize: "16px",
                          }}
                        />
                      </IconButton>
                    )}

                    {/* Delete Modal */}
                    <DeleteModal
                      open={openDeleteModal}
                      setOpen={setOpenDeleteModal}
                      loading={loading}
                      onConfirm={handleDeleteConfirm}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        count={Math.ceil(counts / ROWS_PER_PAGE)}
        page={currentPage}
        onChange={handleChangePage}
      />

      <DeleteModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        loading={loading}
        setLoading={setLoading}
        handleDeletefunction={handleDeleteConfirm}
        id={deleteBillId}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <CustomSnackbar
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default BillerReportsBody;
